
import React, { useState, useContext } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { SettingsSearchContext } from './SettingsContext';

interface CollapsibleSectionProps {
  title: string;
  icon?: string;
  children: React.ReactNode;
  darkMode: boolean;
  defaultOpen?: boolean;
  className?: string;
}

export const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, icon, children, darkMode, defaultOpen = false, className = "" 
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const searchQuery = useContext(SettingsSearchContext);

  // If searching, always show content and hide the toggle header (or keep it expanded)
  const effectiveOpen = searchQuery ? true : isOpen;

  return (
    <div className={`w-full flex flex-col ${className}`}>
      <button 
        onClick={() => !searchQuery && setIsOpen(!isOpen)}
        className={`w-full flex items-center gap-3 py-4 px-1 group transition-all ${searchQuery ? 'cursor-default' : 'cursor-pointer'}`}
      >
        <div className={`flex-1 h-px transition-all ${darkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-neutral-200 group-hover:bg-neutral-300'}`} />
        <div className="flex items-center gap-2.5 px-2">
            {icon && <i className={`fa-solid ${icon} text-[10px] opacity-30 group-hover:opacity-100 transition-opacity`}></i>}
            <span className="text-[11px] font-black uppercase tracking-wider opacity-60 group-hover:opacity-100 transition-opacity">
                {title}
            </span>
            {!searchQuery && (
                <motion.i 
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ type: 'spring', damping: 15 }}
                    className="fa-solid fa-chevron-down text-[8px] opacity-20"
                />
            )}
        </div>
        <div className={`flex-1 h-px transition-all ${darkMode ? 'bg-white/5 group-hover:bg-white/10' : 'bg-neutral-200 group-hover:bg-neutral-300'}`} />
      </button>

      <AnimatePresence initial={false}>
        {effectiveOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.23, 1, 0.32, 1] }}
            className="overflow-hidden"
          >
            <div className="pb-4 pt-1">
                {children}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};
